import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Check, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import SecurityQuestionsFields from "@/components/SecurityQuestionsFields";

/**
 * Settings → Security. Lets the signed-in user see which 2 security
 * questions they currently have on file and replace them. Answers are
 * never returned by the backend (hash-only storage), so when editing we
 * require the user to re-type both answers for the new pair.
 */
export default function Security() {
  const [current, setCurrent] = useState(null); // { has_questions, questions }
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ q1: "", a1: "", q2: "", a2: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/auth/security-questions/mine");
      setCurrent(r.data);
      if (r.data?.questions?.length === 2) {
        setDraft((d) => ({
          ...d,
          q1: r.data.questions[0],
          q2: r.data.questions[1],
        }));
      }
    } catch {
      setCurrent({ has_questions: false, questions: [] });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!draft.q1 || !draft.q2) {
      setError("Please pick both security questions.");
      return;
    }
    if (draft.q1 === draft.q2) {
      setError("Pick two different questions.");
      return;
    }
    if (!draft.a1.trim() || !draft.a2.trim()) {
      setError("Both answers are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/security-questions/set", {
        questions: [
          { question: draft.q1, answer: draft.a1 },
          { question: draft.q2, answer: draft.a2 },
        ],
      });
      setSaved(true);
      setEditing(false);
      setDraft((d) => ({ ...d, a1: "", a2: "" }));
      await load();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="security-page">
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-sm mb-5"
        style={{ color: "var(--text-muted)" }}
        data-testid="security-back-btn"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: "var(--hl-blue)" }}
        >
          <ShieldCheck size={20} style={{ color: "var(--primary)" }} />
        </div>
        <h1 className="font-display text-3xl font-bold">Security questions</h1>
      </div>
      <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
        If you ever forget your password, we'll ask you the two questions
        below to let you back in. Keep the answers memorable — case and
        spaces don't matter.
      </p>

      {current === null ? (
        <div className="pm-card p-6 text-center" data-testid="security-loading">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
        </div>
      ) : !current.has_questions && !editing ? (
        <div className="pm-card p-6" data-testid="security-empty-card">
          <div className="font-semibold mb-1">
            You haven't set up security questions yet.
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Set them up now — it only takes 30 seconds and it's the only way
            to reset your password if you forget it.
          </p>
          <button
            onClick={() => setEditing(true)}
            className="pm-btn-primary"
            data-testid="security-start-setup-btn"
          >
            Set up security questions
          </button>
        </div>
      ) : !editing ? (
        <div className="pm-card p-6" data-testid="security-current-card">
          <div className="space-y-3 mb-5">
            {current.questions.map((q, i) => (
              <div
                key={i}
                data-testid={`security-current-q${i + 1}`}
                className="p-3 rounded-xl flex items-start gap-2"
                style={{ background: "var(--muted)" }}
              >
                <ShieldCheck
                  size={16}
                  className="mt-0.5 flex-none"
                  style={{ color: "var(--primary)" }}
                />
                <div className="text-sm">{q}</div>
              </div>
            ))}
          </div>
          {saved && (
            <div
              className="flex items-center gap-2 text-sm mb-4"
              style={{ color: "#4f7942" }}
              data-testid="security-saved-flash"
            >
              <Check size={14} /> Saved. Your new questions are active.
            </div>
          )}
          <button
            onClick={() => setEditing(true)}
            className="pm-btn-secondary"
            data-testid="security-edit-btn"
          >
            Change questions & answers
          </button>
        </div>
      ) : (
        <form
          onSubmit={save}
          className="pm-card p-6 space-y-4"
          data-testid="security-edit-form"
        >
          <SecurityQuestionsFields
            value={draft}
            onChange={setDraft}
            testIdPrefix="security"
          />
          {error && (
            <div
              className="text-sm p-3 rounded-xl"
              style={{ background: "#fdecea", color: "var(--weakness)" }}
              data-testid="security-error"
            >
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="pm-btn-primary flex items-center justify-center gap-2"
              data-testid="security-save-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
            {current?.has_questions && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError("");
                  setDraft((d) => ({ ...d, a1: "", a2: "" }));
                }}
                className="pm-btn-secondary"
                data-testid="security-cancel-btn"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
